// Guards against XSS via dangerous URL schemes (e.g. `javascript:`, `data:`)
// reaching `window.open` or a location assignment. Only http(s) targets are
// allowed through — relative URLs are fine because they resolve against the
// current http(s) origin. The WHATWG URL parser also strips embedded tabs and
// newlines, so obfuscated schemes like `jav\tascript:` are still rejected.
const SAFE_PROTOCOLS = ['http:', 'https:']

export function isSafeUrl(url: string | null | undefined): url is string {
  if (!url) {
    return false
  }
  try {
    const parsed = new URL(url, window.location.origin)
    return SAFE_PROTOCOLS.includes(parsed.protocol)
  } catch {
    return false
  }
}

// `noopener` is always added because `window.open` (unlike `<a target="_blank">`)
// exposes `window.opener` to the opened page by default, allowing reverse
// tabnabbing.
function withSafeFeatures(features?: string): string {
  const parts = new Set(
    (features ?? '')
      .split(',')
      .map((feature) => feature.trim())
      .filter(Boolean)
  )
  parts.add('noopener')
  parts.add('noreferrer')
  return [...parts].join(',')
}

// Opens `url` in a new context only when it passes `isSafeUrl`, always with
// `noopener,noreferrer`. Returns `null` when the URL was rejected, the popup
// was blocked, or `noopener` withheld the window reference.
export function openSafeUrl(
  url: string | null | undefined,
  target?: string,
  features?: string
): Window | null {
  if (!isSafeUrl(url)) {
    return null
  }
  return window.open(url, target, withSafeFeatures(features))
}
